import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Plus, CheckCircle2, AlertCircle, CalendarDays, ListTodo, Calendar, CalendarRange, Clock
} from "lucide-react";
import { toast } from "sonner";
import { PulseCalendar } from "@/components/pulse-planner/PulseCalendar";
import { PulseWeekView } from "@/components/pulse-planner/PulseWeekView";
import { PulseDayView } from "@/components/pulse-planner/PulseDayView";
import { PulseDayPanel } from "@/components/pulse-planner/PulseDayPanel";
import { PulseEventDialog } from "@/components/pulse-planner/PulseEventDialog";
import { PulseTaskDialog } from "@/components/pulse-planner/PulseTaskDialog";
import { toLocalDateStr, toLocalDateTimeStr } from "@/components/pulse-planner/pulseUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PulsePlannerPage = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("month"); // month | week | day

  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ events_today: 0, tasks_open: 0, tasks_overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [taskFilter, setTaskFilter] = useState("open");

  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingTask, setEditingTask] = useState(null);

  const [eventForm, setEventForm] = useState({
    title: "", start_datetime: "", duration: "60",
    location: "", notes: "", reminder_minutes: "", repeat_rule: ""
  });

  const [taskForm, setTaskForm] = useState({
    title: "", description: "", due_datetime: "",
    priority: "medium", show_on_calendar: false, linked_project_id: ""
  });

  useEffect(() => { loadData(); }, [currentDate, taskFilter, calendarView]);

  const loadData = async () => {
    try {
      let start, end;
      if (calendarView === "week") {
        // Load full week + padding
        const d = new Date(currentDate);
        const day = d.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        start = new Date(d);
        start.setDate(d.getDate() + diff);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59);
      } else if (calendarView === "day") {
        start = new Date(selectedDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(selectedDate);
        end.setHours(23, 59, 59);
      } else {
        // Month view: include padding weeks
        start = new Date(currentDate.getFullYear(), currentDate.getMonth(), -6);
        end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 7, 23, 59, 59);
      }
      const [eventsRes, tasksRes, statsRes] = await Promise.all([
        axios.get(`${API}/pulse/events`, { params: { start: toLocalDateTimeStr(start), end: toLocalDateTimeStr(end) } }),
        axios.get(`${API}/pulse/tasks`, { params: { filter_type: taskFilter } }),
        axios.get(`${API}/pulse/stats`)
      ]);
      setEvents(eventsRes.data);
      setTasks(tasksRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error(t("pulsePlanner.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  const selectedDateEvents = useMemo(() => {
    const dateStr = toLocalDateStr(selectedDate);
    return events.filter(e => toLocalDateStr(new Date(e.start_datetime)) === dateStr)
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
  }, [events, selectedDate]);

  const occupiedTimeSlots = useMemo(() => {
    if (!eventForm.start_datetime) return [];
    const formDate = toLocalDateStr(new Date(eventForm.start_datetime));
    return events
      .filter(e => {
        if (editingEvent && e.id === editingEvent.id) return false;
        return toLocalDateStr(new Date(e.start_datetime)) === formDate;
      })
      .map(e => {
        const d = new Date(e.start_datetime);
        let hours = d.getHours();
        const minutes = d.getMinutes();
        const period = hours >= 12 ? 'PM' : 'AM';
        if (hours === 0) hours = 12;
        else if (hours > 12) hours -= 12;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
      });
  }, [events, eventForm.start_datetime, editingEvent]);

  const navigateMonth = (delta) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1));
  };

  const navigateWeek = (delta) => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + delta * 7);
    setCurrentDate(d);
  };

  const navigateDay = (delta) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + delta);
    setSelectedDate(d);
    setCurrentDate(d);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const handleSelectDate = (date) => {
    setSelectedDate(date);
    // If user clicks a date in month/week view, also switch to show that date
    setCurrentDate(date);
  };

  const calculateDuration = (start, end) => {
    const diffMs = new Date(end) - new Date(start);
    const diffMins = Math.round(diffMs / 60000);
    const durations = [15, 30, 45, 60, 90, 120, 180, 240];
    return durations.reduce((prev, curr) =>
      Math.abs(curr - diffMins) < Math.abs(prev - diffMins) ? curr : prev
    ).toString();
  };

  const openEventDialog = (event = null, date = null) => {
    if (event) {
      setEditingEvent(event);
      setEventForm({
        title: event.title,
        // Convert stored datetime (may be UTC) to local string for the picker
        start_datetime: toLocalDateTimeStr(new Date(event.start_datetime)),
        duration: calculateDuration(event.start_datetime, event.end_datetime),
        location: event.location || "",
        notes: event.notes || "",
        reminder_minutes: event.reminder_minutes || "",
        repeat_rule: event.repeat_rule || ""
      });
    } else {
      setEditingEvent(null);
      const targetDate = date || selectedDate;
      const start = new Date(targetDate);
      // Only default to 9 AM when opening via "New Event" button (no clicked slot)
      if (!date) {
        start.setHours(9, 0, 0, 0);
      }
      setEventForm({
        title: "", start_datetime: toLocalDateTimeStr(start), duration: "60",
        location: "", notes: "", reminder_minutes: "", repeat_rule: ""
      });
    }
    setEventDialogOpen(true);
  };

  const saveEvent = async () => {
    if (!eventForm.title || !eventForm.start_datetime) {
      toast.error(t("pulsePlanner.fillRequiredFields"));
      return;
    }
    try {
      const startDate = new Date(eventForm.start_datetime);
      const endDate = new Date(startDate.getTime() + parseInt(eventForm.duration) * 60000);
      const data = {
        title: eventForm.title,
        // Store as UTC ISO string so backend scheduler compares correctly
        start_datetime: startDate.toISOString(),
        end_datetime: endDate.toISOString(),
        location: eventForm.location || null,
        notes: eventForm.notes || null,
        reminder_minutes: eventForm.reminder_minutes ? parseInt(eventForm.reminder_minutes) : null,
        repeat_rule: eventForm.repeat_rule || null
      };
      if (editingEvent) {
        await axios.put(`${API}/pulse/events/${editingEvent.id}`, data);
        toast.success(t("pulsePlanner.eventUpdated"));
      } else {
        await axios.post(`${API}/pulse/events`, data);
        toast.success(t("pulsePlanner.eventCreated"));
      }
      setEventDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("pulsePlanner.failedToSaveEvent"));
    }
  };

  const deleteEvent = async (eventId) => {
    // For recurring instances, extract the parent ID
    const parentId = eventId.includes("_r") ? eventId.split("_r")[0] : eventId;
    const isRecurring = eventId.includes("_r");
    const msg = isRecurring ? t("pulsePlanner.deleteRecurringConfirm") : t("pulsePlanner.deleteEventConfirm");
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/pulse/events/${parentId}`);
      toast.success(t("pulsePlanner.eventDeleted"));
      loadData();
    } catch (error) {
      toast.error(t("pulsePlanner.failedToDeleteEvent"));
    }
  };

  const handleEventDrop = async (eventId, newStart, newEnd) => {
    try {
      await axios.put(`${API}/pulse/events/${eventId}`, {
        start_datetime: newStart.toISOString(),
        end_datetime: newEnd.toISOString(),
      });
      toast.success(t("pulsePlanner.eventRescheduled"));
      loadData();
    } catch (error) {
      toast.error(t("pulsePlanner.failedToReschedule"));
    }
  };

  const handleSlotClick = (datetime) => {
    openEventDialog(null, datetime);
  };

  const handleEventClick = (event) => {
    if (event.id?.includes("_r")) {
      // For recurring instances, open the parent for editing
      const parentId = event.id.split("_r")[0];
      const parentEvent = events.find(e => e.id === parentId) || event;
      openEventDialog(parentEvent);
    } else {
      openEventDialog(event);
    }
  };

  const openTaskDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setTaskForm({
        title: task.title,
        description: task.description || "",
        due_datetime: task.due_datetime ? task.due_datetime.slice(0, 16) : "",
        priority: task.priority || "medium",
        show_on_calendar: task.show_on_calendar || false,
        linked_project_id: task.linked_project_id || ""
      });
    } else {
      setEditingTask(null);
      const targetDate = new Date(selectedDate);
      targetDate.setHours(10, 0, 0, 0);
      setTaskForm({
        title: "", description: "",
        due_datetime: toLocalDateTimeStr(targetDate),
        priority: "medium", show_on_calendar: true, linked_project_id: ""
      });
    }
    setTaskDialogOpen(true);
  };

  const saveTask = async () => {
    if (!taskForm.title) {
      toast.error(t("pulsePlanner.enterTaskTitle"));
      return;
    }
    try {
      const data = {
        ...taskForm,
        due_datetime: taskForm.due_datetime ? toLocalDateTimeStr(new Date(taskForm.due_datetime)) : null,
        linked_project_id: taskForm.linked_project_id || null
      };
      if (editingTask) {
        await axios.put(`${API}/pulse/tasks/${editingTask.id}`, data);
        toast.success(t("pulsePlanner.taskUpdated"));
      } else {
        await axios.post(`${API}/pulse/tasks`, data);
        toast.success(t("pulsePlanner.taskCreated"));
      }
      setTaskDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("pulsePlanner.failedToSaveTask"));
    }
  };

  const toggleTaskComplete = async (task) => {
    try {
      if (task.status === "completed") {
        await axios.put(`${API}/pulse/tasks/${task.id}`, { status: "open" });
      } else {
        await axios.post(`${API}/pulse/tasks/${task.id}/complete`);
      }
      loadData();
    } catch (error) {
      toast.error(t("pulsePlanner.failedToUpdateTask"));
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm(t("pulsePlanner.deleteTaskConfirm"))) return;
    try {
      await axios.delete(`${API}/pulse/tasks/${taskId}`);
      toast.success(t("pulsePlanner.taskDeleted"));
      loadData();
    } catch (error) {
      toast.error(t("pulsePlanner.failedToDeleteTask"));
    }
  };

  const convertToTeamTask = async (taskId) => {
    try {
      await axios.post(`${API}/pulse/tasks/${taskId}/convert-to-team`);
      toast.success(t("pulsePlanner.taskConverted"));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("pulsePlanner.failedToConvertTask"));
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-500">{t("pulsePlanner.loadingPlanner")}</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div data-testid="pulse-planner-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest mb-1">{t("pulsePlanner.personal")}</p>
            <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight uppercase">{t("pulsePlanner.pageTitle")}</h1>
            <p className="text-sm text-slate-500 mt-1">{t("pulsePlanner.pageSubtitle")}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => openTaskDialog()} variant="outline" className="rounded-sm" data-testid="add-task-btn">
              <ListTodo className="w-4 h-4 mr-2" />{t("pulsePlanner.addTask")}
            </Button>
            <Button onClick={() => openEventDialog()} className="bg-indigo-600 hover:bg-indigo-700 rounded-sm" data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-2" />{t("pulsePlanner.addEvent")}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-50 rounded-sm flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="font-mono text-2xl font-semibold text-slate-900">{stats.events_today}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{t("pulsePlanner.eventsToday")}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-sm flex items-center justify-center">
                <ListTodo className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-mono text-2xl font-semibold text-slate-900">{stats.tasks_open}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{t("pulsePlanner.openTasks")}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-sm flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="font-mono text-2xl font-semibold text-slate-900">{stats.tasks_overdue}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{t("pulsePlanner.overdue")}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-sm flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-mono text-2xl font-semibold text-slate-900">{stats.tasks_completed}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{t("pulsePlanner.completed")}</p>
              </div>
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-sm p-1 w-fit">
          <Button
            variant={calendarView === "month" ? "default" : "ghost"}
            size="sm"
            onClick={() => setCalendarView("month")}
            className={`rounded-sm h-8 px-3 ${calendarView === "month" ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}`}
            data-testid="view-month-btn"
          >
            <Calendar className="w-3.5 h-3.5 mr-1.5" />{t("pulsePlanner.month")}
          </Button>
          <Button
            variant={calendarView === "week" ? "default" : "ghost"}
            size="sm"
            onClick={() => setCalendarView("week")}
            className={`rounded-sm h-8 px-3 ${calendarView === "week" ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}`}
            data-testid="view-week-btn"
          >
            <CalendarRange className="w-3.5 h-3.5 mr-1.5" />{t("pulsePlanner.week")}
          </Button>
          <Button
            variant={calendarView === "day" ? "default" : "ghost"}
            size="sm"
            onClick={() => setCalendarView("day")}
            className={`rounded-sm h-8 px-3 ${calendarView === "day" ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}`}
            data-testid="view-day-btn"
          >
            <Clock className="w-3.5 h-3.5 mr-1.5" />{t("pulsePlanner.day")}
          </Button>
        </div>

        {/* Main Content */}
        {calendarView === "month" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PulseCalendar
              currentDate={currentDate}
              selectedDate={selectedDate}
              events={events}
              tasks={tasks}
              onNavigateMonth={navigateMonth}
              onGoToToday={goToToday}
              onSelectDate={handleSelectDate}
              onEventDrop={handleEventDrop}
            />
            <PulseDayPanel
              selectedDate={selectedDate}
              selectedDateEvents={selectedDateEvents}
              tasks={tasks}
              taskFilter={taskFilter}
              onTaskFilterChange={setTaskFilter}
              onOpenEventDialog={openEventDialog}
              onOpenTaskDialog={openTaskDialog}
              onDeleteEvent={deleteEvent}
              onToggleTaskComplete={toggleTaskComplete}
              onDeleteTask={deleteTask}
              onConvertToTeamTask={convertToTeamTask}
              onEditEvent={(event) => openEventDialog(event)}
              onEditTask={(task) => openTaskDialog(task)}
            />
          </div>
        )}

        {calendarView === "week" && (
          <PulseWeekView
            currentDate={currentDate}
            selectedDate={selectedDate}
            events={events}
            onNavigateWeek={navigateWeek}
            onGoToToday={goToToday}
            onSelectDate={handleSelectDate}
            onEventClick={handleEventClick}
            onSlotClick={handleSlotClick}
            onEventDrop={handleEventDrop}
          />
        )}

        {calendarView === "day" && (
          <PulseDayView
            currentDate={currentDate}
            selectedDate={selectedDate}
            events={events}
            tasks={tasks}
            onNavigateDay={navigateDay}
            onGoToToday={goToToday}
            onEventClick={handleEventClick}
            onSlotClick={handleSlotClick}
            onEventDrop={handleEventDrop}
          />
        )}
      </div>

      <PulseEventDialog
        open={eventDialogOpen}
        onOpenChange={setEventDialogOpen}
        eventForm={eventForm}
        setEventForm={setEventForm}
        onSave={saveEvent}
        onDelete={deleteEvent}
        editingEvent={editingEvent}
        occupiedTimeSlots={occupiedTimeSlots}
      />

      <PulseTaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        taskForm={taskForm}
        setTaskForm={setTaskForm}
        onSave={saveTask}
        onDelete={deleteTask}
        editingTask={editingTask}
      />
    </Layout>
  );
};

export default PulsePlannerPage;
